import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LEAD_SORTABLE_COLUMNS, PROFILE_SORT_ORDER_OPTIONS } from '../../../config/enum-options.js';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi, normalizeList } from '../../../lib/admin-api/index.js';
import { useFilterPresets } from '../../../shared/filter-presets/use-filter-presets.js';
import { buildLeadsRequestDto, mapLeadsResponseToDto } from '../dto/leads-list.dto.js';
import { buildLeadsTableModel } from '../model/leads-list.model.js';

export const DEFAULT_FILTERS = Object.freeze({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: '',
  language: '',
  status: '',
  collectionSource: '',
  lawfulBasis: '',
  consentStatus: '',
  privacyNoticeStatus: '',
  assignedOnly: false,
  matchedOnly: false,
  doNotContact: false,
});

export const DEFAULT_SORT = Object.freeze({
  field: LEAD_SORTABLE_COLUMNS.created,
  order: PROFILE_SORT_ORDER_OPTIONS.desc,
});

export const LEAD_PAGE_SIZE = 20;

const LEAD_FILTER_STORAGE_KEY = 'admin_leads_list_filters';
const LEAD_FILTER_PRESET_STORAGE_KEY = 'admin_lead_filter_presets';
export const LEAD_FORM_DEFAULTS = Object.freeze({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: '',
  language: '',
  status: 'new',
  collectionSource: 'manual',
  collectionContext: '',
  lawfulBasis: 'legitimate_interest',
  consentStatus: 'not_requested',
  marketingConsentEmail: false,
  marketingConsentPhone: false,
  privacyNoticeStatus: 'pending',
  privacyNoticeSentAt: '',
  doNotContact: false,
  details: Object.freeze([]),
});

function readSearchParams() {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
}

function readStringParam(params, key) {
  return String(params.get(key) || '').trim();
}

function readBooleanParam(params, key) {
  const value = String(params.get(key) || '').trim().toLowerCase();
  return value === '1' || value === 'true';
}

function getInitialFiltersFromUrl() {
  const params = readSearchParams();

  return {
    firstName: readStringParam(params, 'firstName'),
    lastName: readStringParam(params, 'lastName'),
    email: readStringParam(params, 'email'),
    phone: readStringParam(params, 'phone'),
    country: readStringParam(params, 'country'),
    language: readStringParam(params, 'language'),
    status: readStringParam(params, 'status'),
    collectionSource: readStringParam(params, 'collectionSource'),
    lawfulBasis: readStringParam(params, 'lawfulBasis'),
    consentStatus: readStringParam(params, 'consentStatus'),
    privacyNoticeStatus: readStringParam(params, 'privacyNoticeStatus'),
    assignedOnly: readBooleanParam(params, 'assignedOnly'),
    matchedOnly: readBooleanParam(params, 'matchedOnly'),
    doNotContact: readBooleanParam(params, 'doNotContact'),
  };
}

function readStoredFilters() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(LEAD_FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeLeadFilters(filters) {
  return {
    ...DEFAULT_FILTERS,
    ...filters,
    firstName: String(filters?.firstName || '').trim(),
    lastName: String(filters?.lastName || '').trim(),
    email: String(filters?.email || '').trim(),
    phone: String(filters?.phone || '').trim(),
    country: String(filters?.country || '').trim().toUpperCase(),
    language: String(filters?.language || '').trim().toLowerCase(),
    status: String(filters?.status || '').trim(),
    collectionSource: String(filters?.collectionSource || '').trim(),
    lawfulBasis: String(filters?.lawfulBasis || '').trim(),
    consentStatus: String(filters?.consentStatus || '').trim(),
    privacyNoticeStatus: String(filters?.privacyNoticeStatus || '').trim(),
    assignedOnly: filters?.assignedOnly === true,
    matchedOnly: filters?.matchedOnly === true,
    doNotContact: filters?.doNotContact === true,
  };
}

function hasActiveFilters(filters) {
  const normalized = normalizeLeadFilters(filters);
  return Boolean(
    normalized.firstName
      || normalized.lastName
      || normalized.email
      || normalized.phone
      || normalized.country
      || normalized.language
      || normalized.status
      || normalized.collectionSource
      || normalized.lawfulBasis
      || normalized.consentStatus
      || normalized.privacyNoticeStatus
      || normalized.assignedOnly
      || normalized.matchedOnly
      || normalized.doNotContact,
  );
}

function getInitialLeadFilters() {
  const urlFilters = normalizeLeadFilters(getInitialFiltersFromUrl());
  if (hasActiveFilters(urlFilters)) {
    return urlFilters;
  }

  const storedFilters = readStoredFilters();
  return normalizeLeadFilters(storedFilters || DEFAULT_FILTERS);
}

function buildFilterSearchParams(filters) {
  const normalized = normalizeLeadFilters(filters);
  const params = new URLSearchParams();

  [
    'firstName',
    'lastName',
    'email',
    'phone',
    'country',
    'language',
    'status',
    'collectionSource',
    'lawfulBasis',
    'consentStatus',
    'privacyNoticeStatus',
  ].forEach((key) => {
    if (normalized[key]) {
      params.set(key, normalized[key]);
    }
  });

  if (normalized.assignedOnly) {
    params.set('assignedOnly', '1');
  }
  if (normalized.matchedOnly) {
    params.set('matchedOnly', '1');
  }
  if (normalized.doNotContact) {
    params.set('doNotContact', '1');
  }

  return params;
}

function persistLeadFilters(filters) {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = normalizeLeadFilters(filters);
  const active = hasActiveFilters(normalized);

  try {
    if (active) {
      window.sessionStorage.setItem(LEAD_FILTER_STORAGE_KEY, JSON.stringify(normalized));
    } else {
      window.sessionStorage.removeItem(LEAD_FILTER_STORAGE_KEY);
    }
  } catch {
    // ignore unavailable storage
  }

  const params = buildFilterSearchParams(normalized);
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

export function normalizeFormString(value) {
  const normalized = String(value || '').trim();
  return normalized === '-' ? '' : normalized;
}

function normalizeDateTimeLocal(value) {
  const raw = normalizeFormString(value);
  if (!raw) {
    return '';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw.slice(0, 16);
  }

  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function normalizeLeadFormFromRow(row = null) {
  if (!row) {
    return {
      ...LEAD_FORM_DEFAULTS,
      details: [],
    };
  }

  const source = row.source || row;
  return {
    ...LEAD_FORM_DEFAULTS,
    firstName: normalizeFormString(pickFirst(source.firstName, source.first_name)),
    lastName: normalizeFormString(pickFirst(source.lastName, source.last_name)),
    email: normalizeFormString(pickFirst(source.email, row.email)),
    phone: normalizeFormString(pickFirst(source.phone, row.phone)),
    country: normalizeFormString(pickFirst(source.country, row.country)).toUpperCase(),
    language: normalizeFormString(pickFirst(source.language, row.language)).toLowerCase(),
    status: normalizeFormString(pickFirst(source.status, row.status)) || LEAD_FORM_DEFAULTS.status,
    collectionSource: normalizeFormString(pickFirst(source.collectionSource, row.collectionSource)) || LEAD_FORM_DEFAULTS.collectionSource,
    collectionContext: normalizeFormString(pickFirst(source.collectionContext, row.collectionContext)),
    lawfulBasis: normalizeFormString(pickFirst(source.lawfulBasis, row.lawfulBasis)) || LEAD_FORM_DEFAULTS.lawfulBasis,
    consentStatus: normalizeFormString(pickFirst(source.consentStatus, row.consentStatus)) || LEAD_FORM_DEFAULTS.consentStatus,
    marketingConsentEmail: source.marketingConsentEmail === true || row.marketingConsentEmail === true,
    marketingConsentPhone: source.marketingConsentPhone === true || row.marketingConsentPhone === true,
    privacyNoticeStatus: normalizeFormString(pickFirst(source.privacyNoticeStatus, row.privacyNoticeStatus)) || LEAD_FORM_DEFAULTS.privacyNoticeStatus,
    privacyNoticeSentAt: normalizeDateTimeLocal(pickFirst(source.privacyNoticeSentAt, row.privacyNoticeSentAt)),
    doNotContact: source.doNotContact === true || row.doNotContact === true,
    details: [],
  };
}

function normalizeLeadDetails(payload) {
  const raw = payload?.details ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
  return list
    .map((item) => ({
      name: normalizeFormString(item?.name),
      value: normalizeFormString(item?.value),
    }))
    .filter((item) => item.name || item.value);
}

function normalizePrivacyNoticeSentAt(value) {
  const normalized = normalizeFormString(value);
  if (!normalized) {
    return null;
  }

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? normalized : date.toISOString();
}

export function buildLeadFormPayload(form, includeDetails = true) {
  const details = (Array.isArray(form.details) ? form.details : [])
    .map((item) => ({
      name: normalizeFormString(item?.name),
      value: normalizeFormString(item?.value),
    }))
    .filter((item) => item.name || item.value);

  const payload = {
    firstName: normalizeFormString(form.firstName) || null,
    lastName: normalizeFormString(form.lastName) || null,
    email: normalizeFormString(form.email) || null,
    phone: normalizeFormString(form.phone) || null,
    country: normalizeFormString(form.country).toUpperCase() || null,
    language: normalizeFormString(form.language).toLowerCase() || null,
    status: normalizeFormString(form.status) || null,
    collectionSource: normalizeFormString(form.collectionSource) || null,
    collectionContext: normalizeFormString(form.collectionContext) || null,
    lawfulBasis: normalizeFormString(form.lawfulBasis) || null,
    consentStatus: normalizeFormString(form.consentStatus) || null,
    marketingConsentEmail: form.marketingConsentEmail === true,
    marketingConsentPhone: form.marketingConsentPhone === true,
    privacyNoticeStatus: normalizeFormString(form.privacyNoticeStatus) || null,
    privacyNoticeSentAt: normalizePrivacyNoticeSentAt(form.privacyNoticeSentAt),
    doNotContact: form.doNotContact === true,
  };

  if (includeDetails) {
    payload.details = details;
  }

  return payload;
}

export function validateLeadForm(form, includeDetails = true, requireUpdateFields = false) {
  const errors = {};
  if (!normalizeFormString(form.email) && !normalizeFormString(form.phone)) {
    errors.contact = 'Email or phone is required';
  }

  if (requireUpdateFields) {
    ['firstName', 'lastName', 'country', 'phone', 'email'].forEach((field) => {
      if (!normalizeFormString(form[field])) {
        errors[field] = 'Required for lead update';
      }
    });
    if (errors.firstName || errors.lastName || errors.country || errors.phone || errors.email) {
      errors.contract = 'Required fields are missing';
    }
  }

  const invalidDetail = includeDetails && (Array.isArray(form.details) ? form.details : []).some((item) => {
    const name = normalizeFormString(item?.name);
    const value = normalizeFormString(item?.value);
    return (name && !value) || (!name && value);
  });
  if (invalidDetail) {
    errors.details = 'Detail name and value are required together';
  }

  return errors;
}

export function mapAdminProfileOptions(payload) {
  return normalizeList(payload)
    .map((profile, index) => {
      const id = String(pickFirst(profile?.uid, profile?.id) || '').trim();
      if (!id) {
        return null;
      }

      const firstName = pickFirst(profile?.firstName, profile?.first_name);
      const lastName = pickFirst(profile?.lastName, profile?.last_name);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const fallbackName = pickFirst(
        profile?.name,
        profile?.full_name,
        profile?.username,
        profile?.emailMasked,
        profile?.email_masked,
        `Profile ${index + 1}`,
      );
      const label = fullName || String(fallbackName || '').trim() || id;
      return { id, label };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function mapCountryOptions(payload) {
  const raw = payload?.countries ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);

  const options = list
    .map((item) => {
      if (typeof item === 'string') {
        const value = item.trim().toUpperCase();
        return value ? { value, label: value } : null;
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.code || item.value || item.country || '').trim().toUpperCase();
      if (!value) {
        return null;
      }

      const name = String(item.name || item.label || '').trim();
      return { value, label: name ? `${value} - ${name}` : value };
    })
    .filter(Boolean);

  const uniq = new Map();
  options.forEach((option) => uniq.set(option.value, option));
  return Array.from(uniq.values()).sort((a, b) => a.value.localeCompare(b.value));
}

export function useLeadsList() {
  const { token } = useAuth();
  const leadFormDetailsRequestRef = useRef(0);
  const initialFilters = useMemo(() => getInitialLeadFilters(), []);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [isFilterOpen, setIsFilterOpen] = useState(() => hasActiveFilters(initialFilters));
  const [rows, setRows] = useState([]);
  const [hasMoreLeads, setHasMoreLeads] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignTargetLeadId, setAssignTargetLeadId] = useState('');
  const [assignTargetLeadName, setAssignTargetLeadName] = useState('');
  const [adminProfileOptions, setAdminProfileOptions] = useState([]);
  const [isAdminProfilesLoading, setIsAdminProfilesLoading] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [leadFormTarget, setLeadFormTarget] = useState(null);
  const [leadForm, setLeadForm] = useState(() => normalizeLeadFormFromRow(null));
  const [leadFormErrors, setLeadFormErrors] = useState({});
  const [leadFormError, setLeadFormError] = useState('');
  const [isLeadFormSaving, setIsLeadFormSaving] = useState(false);
  const [isLeadFormLoadingDetails, setIsLeadFormLoadingDetails] = useState(false);
  const [shouldSubmitLeadDetails, setShouldSubmitLeadDetails] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetLead, setDeleteTargetLead] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeletingLead, setIsDeletingLead] = useState(false);

  const applyPresetFilters = useCallback((presetFilters) => {
    setFilters(normalizeLeadFilters(presetFilters));
    setIsFilterOpen(true);
  }, []);

  const filterPresetState = useFilterPresets({
    storageKey: LEAD_FILTER_PRESET_STORAGE_KEY,
    filters,
    onApply: applyPresetFilters,
  });

  useEffect(() => {
    persistLeadFilters(filters);
  }, [filters]);

  const loadLeadsPage = useCallback(async ({ offset = 0, append = false } = {}) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError('');

    try {
      const payload = buildLeadsRequestDto(filters, sort, {
        limit: LEAD_PAGE_SIZE,
        offset,
      });
      const response = await adminApi.getLeads(token, payload);
      const dto = mapLeadsResponseToDto(response);
      const nextRows = buildLeadsTableModel(dto);
      setRows((previousRows) => (append ? [...previousRows, ...nextRows] : nextRows));
      setHasMoreLeads(nextRows.length === LEAD_PAGE_SIZE);
    } catch (requestError) {
      if (!append) {
        setRows([]);
        setHasMoreLeads(false);
      }
      setError(requestError.message || 'Failed to load leads');
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [token, filters, sort]);

  useEffect(() => {
    loadLeadsPage({ offset: 0, append: false });
  }, [loadLeadsPage]);

  const loadMoreLeads = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMoreLeads) {
      return;
    }

    loadLeadsPage({ offset: rows.length, append: true });
  }, [hasMoreLeads, isLoading, isLoadingMore, loadLeadsPage, rows.length]);

  useEffect(() => {
    let isCancelled = false;

    const loadCountries = async () => {
      try {
        const response = await adminApi.getCountryDictionary(token);
        if (!isCancelled) {
          setCountryOptions(mapCountryOptions(response));
        }
      } catch {
        if (!isCancelled) {
          setCountryOptions([]);
        }
      }
    };

    loadCountries();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const sortIndicator = useMemo(
    () => (field) => {
      if (sort.field !== field) {
        return 'fa fa-sort text-muted';
      }

      return sort.order === PROFILE_SORT_ORDER_OPTIONS.asc ? 'fa fa-sort-up' : 'fa fa-sort-down';
    },
    [sort],
  );

  const toggleSort = useCallback((field) => {
    if (!field) {
      return;
    }

    setSort((previous) => {
      if (previous.field !== field) {
        return { field, order: PROFILE_SORT_ORDER_OPTIONS.asc };
      }

      return {
        field,
        order:
          previous.order === PROFILE_SORT_ORDER_OPTIONS.asc
            ? PROFILE_SORT_ORDER_OPTIONS.desc
            : PROFILE_SORT_ORDER_OPTIONS.asc,
      };
    });
  }, []);

  const updateFilter = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setFilters((prev) => normalizeLeadFilters({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  const resetAndCloseFilters = useCallback(() => {
    setFilters(normalizeLeadFilters(DEFAULT_FILTERS));
    setIsFilterOpen(false);
  }, []);

  const loadAdminProfiles = useCallback(async () => {
    setIsAdminProfilesLoading(true);
    setAssignmentError('');

    try {
      const response = await adminApi.getProfiles(token, { isAdmin: true, limit: 100 });
      setAdminProfileOptions(mapAdminProfileOptions(response));
    } catch (requestError) {
      setAdminProfileOptions([]);
      setAssignmentError(requestError.message || 'Failed to load admin profiles');
    } finally {
      setIsAdminProfilesLoading(false);
    }
  }, [token]);

  const openAssignModal = useCallback(async (row) => {
    const leadId = String(row?.id || '').trim();
    if (!leadId) {
      return;
    }

    setAssignTargetLeadId(leadId);
    setAssignTargetLeadName(String(row?.name || '').trim() || '-');
    setSelectedManagerId(String(row?.assignedToId || '').trim());
    setAssignmentError('');
    setIsAssignModalOpen(true);
    await loadAdminProfiles();
  }, [loadAdminProfiles]);

  const closeAssignModal = useCallback((force = false) => {
    if (isAssignmentSaving && !force) {
      return;
    }
    setIsAssignModalOpen(false);
    setAssignTargetLeadId('');
    setAssignTargetLeadName('');
    setSelectedManagerId('');
    setAssignmentError('');
  }, [isAssignmentSaving]);

  const assignManager = useCallback(async () => {
    if (!assignTargetLeadId) {
      setAssignmentError('Lead id is required');
      return;
    }

    const managerId = String(selectedManagerId || '').trim();
    if (!managerId) {
      setAssignmentError('Select a profile to assign');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.assignLead(token, assignTargetLeadId, managerId);
      closeAssignModal(true);
      await loadLeadsPage({ offset: 0, append: false });
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to assign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  }, [assignTargetLeadId, selectedManagerId, token, closeAssignModal, loadLeadsPage]);

  const unassignManager = useCallback(async () => {
    if (!assignTargetLeadId) {
      setAssignmentError('Lead id is required');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.unassignLead(token, assignTargetLeadId);
      closeAssignModal(true);
      await loadLeadsPage({ offset: 0, append: false });
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to unassign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  }, [assignTargetLeadId, token, closeAssignModal, loadLeadsPage]);

  const openLeadForm = useCallback(async (row = null) => {
    const target = row || null;
    const requestId = leadFormDetailsRequestRef.current + 1;
    leadFormDetailsRequestRef.current = requestId;
    setLeadFormTarget(target);
    setLeadForm(normalizeLeadFormFromRow(target));
    setLeadFormErrors({});
    setLeadFormError('');
    setShouldSubmitLeadDetails(!target);
    setIsLeadFormOpen(true);
    const leadId = normalizeFormString(target?.id);
    if (!leadId) {
      return;
    }

    setIsLeadFormLoadingDetails(true);
    try {
      const response = await adminApi.getLeadDetails(token, leadId);
      if (leadFormDetailsRequestRef.current !== requestId) {
        return;
      }
      setLeadForm((previous) => ({
        ...previous,
        details: normalizeLeadDetails(response),
      }));
      setShouldSubmitLeadDetails(true);
    } catch (requestError) {
      if (leadFormDetailsRequestRef.current !== requestId) {
        return;
      }
      setLeadFormError(requestError.message || 'Failed to load lead details');
    } finally {
      if (leadFormDetailsRequestRef.current === requestId) {
        setIsLeadFormLoadingDetails(false);
      }
    }
  }, [token]);

  const closeLeadForm = useCallback((force = false) => {
    if (isLeadFormSaving && !force) {
      return;
    }
    leadFormDetailsRequestRef.current += 1;
    setIsLeadFormOpen(false);
    setLeadFormTarget(null);
    setLeadForm(normalizeLeadFormFromRow(null));
    setLeadFormErrors({});
    setLeadFormError('');
    setIsLeadFormLoadingDetails(false);
    setShouldSubmitLeadDetails(true);
  }, [isLeadFormSaving]);

  const onLeadFormChange = useCallback((field, value) => {
    setLeadForm((previous) => {
      const path = String(field || '').split('.');
      if (path[0] === 'details' && path.length === 3) {
        const index = Number.parseInt(path[1], 10);
        const key = path[2];
        if (!Number.isInteger(index) || index < 0 || !['name', 'value'].includes(key)) {
          return previous;
        }

        const details = [...previous.details];
        details[index] = {
          name: '',
          value: '',
          ...(details[index] || {}),
          [key]: value,
        };
        return {
          ...previous,
          details,
        };
      }

      return {
        ...previous,
        [field]: value,
      };
    });
    if (String(field || '').startsWith('details.')) {
      setShouldSubmitLeadDetails(true);
    }
    setLeadFormErrors({});
  }, []);

  const addLeadDetailRow = useCallback(() => {
    setLeadForm((previous) => ({
      ...previous,
      details: [...previous.details, { name: '', value: '' }],
    }));
    setShouldSubmitLeadDetails(true);
  }, []);

  const removeLeadDetailRow = useCallback((index) => {
    setLeadForm((previous) => ({
      ...previous,
      details: previous.details.filter((_, itemIndex) => itemIndex !== index),
    }));
    setShouldSubmitLeadDetails(true);
  }, []);

  const saveLeadForm = useCallback(async (event) => {
    event?.preventDefault?.();
    const leadId = normalizeFormString(leadFormTarget?.id);
    const errors = validateLeadForm(leadForm, shouldSubmitLeadDetails, Boolean(leadId));
    setLeadFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setLeadFormError('');
      return;
    }

    const payload = buildLeadFormPayload(leadForm, shouldSubmitLeadDetails);
    setIsLeadFormSaving(true);
    setLeadFormError('');

    try {
      if (leadId) {
        await adminApi.updateLead(token, leadId, payload);
      } else {
        await adminApi.createLead(token, payload);
      }
      closeLeadForm(true);
      await loadLeadsPage({ offset: 0, append: false });
    } catch (requestError) {
      setLeadFormError(requestError.message || 'Failed to save lead');
    } finally {
      setIsLeadFormSaving(false);
    }
  }, [closeLeadForm, leadForm, leadFormTarget, loadLeadsPage, shouldSubmitLeadDetails, token]);

  const openDeleteModal = useCallback((row = null) => {
    setDeleteTargetLead(row || null);
    setDeleteReason('');
    setDeleteError('');
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback((force = false) => {
    if (isDeletingLead && !force) {
      return;
    }
    setIsDeleteModalOpen(false);
    setDeleteTargetLead(null);
    setDeleteReason('');
    setDeleteError('');
  }, [isDeletingLead]);

  const onDeleteReasonChange = useCallback((value) => {
    setDeleteReason(String(value || ''));
    setDeleteError('');
  }, []);

  const confirmDeleteLead = useCallback(async () => {
    const leadId = normalizeFormString(deleteTargetLead?.id);
    const reason = normalizeFormString(deleteReason);
    if (!leadId) {
      setDeleteError('Lead id is required');
      return;
    }
    if (reason.length < 3) {
      setDeleteError('Delete reason is required');
      return;
    }

    setIsDeletingLead(true);
    setDeleteError('');
    try {
      await adminApi.deleteLead(token, leadId, { reason });
      closeDeleteModal(true);
      await loadLeadsPage({ offset: 0, append: false });
    } catch (requestError) {
      setDeleteError(requestError.message || 'Failed to delete lead');
    } finally {
      setIsDeletingLead(false);
    }
  }, [closeDeleteModal, deleteReason, deleteTargetLead, loadLeadsPage, token]);

  return {
    rows,
    countryOptions,
    filters,
    sort,
    isFilterOpen,
    isLoading,
    isLoadingMore,
    hasMoreLeads,
    error,
    toggleSort,
    updateFilter,
    resetAndCloseFilters,
    setIsFilterOpen,
    isAssignModalOpen,
    assignTargetLeadName,
    adminProfileOptions,
    isAdminProfilesLoading,
    selectedManagerId,
    isAssignmentSaving,
    assignmentError,
    openAssignModal,
    closeAssignModal,
    onSelectManager: (managerId) => setSelectedManagerId(String(managerId || '').trim()),
    assignManager,
    unassignManager,
    isLeadFormOpen,
    leadFormTarget,
    leadForm,
    leadFormErrors,
    leadFormError,
    isLeadFormSaving,
    isLeadFormLoadingDetails,
    onLeadFormChange,
    addLeadDetailRow,
    removeLeadDetailRow,
    saveLeadForm,
    openLeadForm,
    closeLeadForm,
    isDeleteModalOpen,
    deleteTargetLead,
    deleteReason,
    deleteError,
    isDeletingLead,
    openDeleteModal,
    closeDeleteModal,
    onDeleteReasonChange,
    confirmDeleteLead,
    sortIndicator,
    loadMoreLeads,
    ...filterPresetState,
    reload: () => loadLeadsPage({ offset: 0, append: false }),
  };
}
